// components/popup/PersonSearchPopup.tsx
import React, { useState, useEffect, Dispatch, SetStateAction } from "react";
import { BasePopup } from "./BasePopup";
import UserForm from "../form/UserForm";
import { useTooltip } from "context/TooltipProvider";
import { Person } from "types";

interface PersonSearchPopupProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (e: React.FormEvent) => void;
  onInputChange: (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => void;
  onSearchPerson: (e: React.FormEvent) => void;
  onPersonSelect: (personId: string) => void;
  onUserSubmit: (data: any, isEditMode: boolean) => void;
  formData: any;
  loading: boolean;
  error: string | null;
  searchFayda: string;
  setSearchFayda: (value: string) => void;
  searchPhone: string;
  setSearchPhone: (value: string) => void;
  searchedPersons: any[];
  selectedPerson: any;
  selectedPersonId: string;
  searchLoading: boolean;
  userFormLoading: boolean;
  title: string;
  description: string;
  children: React.ReactNode;
  validateForm?: () => string;
  isEditMode?: boolean;
  existingData?: any;
  setSelectedPerson?: Dispatch<SetStateAction<Person | null>>;
  setSelectedPersonId?: Dispatch<SetStateAction<string>>;
  setSearchedPersons?: Dispatch<SetStateAction<Person[]>>;
}

export const PersonSearchPopup: React.FC<PersonSearchPopupProps> = ({
  isOpen,
  onClose,
  onSubmit,
  onInputChange,
  onSearchPerson,
  onPersonSelect,
  onUserSubmit,
  formData,
  loading,
  error,
  searchFayda,
  setSearchFayda,
  searchPhone,
  setSearchPhone,
  searchedPersons,
  selectedPerson,
  selectedPersonId,
  searchLoading,
  userFormLoading,
  title,
  description,
  children,
  validateForm,
  isEditMode = false,
  existingData,
  setSelectedPersonId,
  setSelectedPerson,
  setSearchedPersons,
}) => {
  const [showUserForm, setShowUserForm] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [isEditingPerson, setIsEditingPerson] = useState(false);
  const { showTooltip } = useTooltip();

  // Reset states when popup closes
  useEffect(() => {
    if (!isOpen) {
      setShowUserForm(false);
      setHasSearched(false);
      setIsEditingPerson(false);
      setSearchFayda("")
      setSearchPhone("")
    }
  }, [isOpen, setSearchFayda, setSearchPhone]);

  // Auto-select if only one result
  useEffect(() => {
    if (searchedPersons?.length === 1 && !selectedPerson && !isEditMode) {
      onPersonSelect(searchedPersons[0].id_number);
    }
  }, [searchedPersons, selectedPerson, onPersonSelect, isEditMode]);


  useEffect(() => {
    if (isEditMode && existingData && isOpen) {
      // Pre-select the existing person
      if (existingData.person) {
        setSelectedPerson?.(existingData.person);
        setSelectedPersonId?.(existingData.person.id_number);
        // Add the existing person to searched persons if not already there
        if (
          !searchedPersons.some(
            (p) => p.id_number === existingData.person.id_number
          )
        ) {
          setSearchedPersons?.([existingData.person]);
        }
      }
    }
  }, [
    isEditMode,
    existingData,
    isOpen,
    searchedPersons,
    setSelectedPerson,
    setSelectedPersonId,
    setSearchedPersons,
  ]);
  const handleSearch = (e: React.FormEvent) => {
    setHasSearched(true);
    onSearchPerson(e);
  };

  const handleAddUser = () => {
    setShowUserForm(true);
    setIsEditingPerson(false);
  };


  const handleUserFormClose = () => {
    setShowUserForm(false);
    setIsEditingPerson(false);
  };

  const handleUserSubmit = (data: any, isUserEditMode: boolean) => {
    onUserSubmit(data, isUserEditMode);
    if (!isUserEditMode) {
      // After successful user creation, auto-select the created user
      setShowUserForm(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (validateForm) {
      const validationError = validateForm();
      if (validationError) {
        showTooltip(validationError, "error");
        return;
      }
    }

    if (!selectedPerson && !isEditMode) {
      showTooltip("Please select or create a person first", "error");
      return;
    }

    onSubmit(e);
  };

  const showAddUserButton =
    hasSearched &&
    searchedPersons?.length === 0 &&
    !showUserForm &&
    !isEditMode;
  const isFormDisabled = !selectedPerson && !isEditMode;

  // Helper function to get button text
  const getSubmitButtonText = () => {
    if (loading) {
      return "Processing...";
    }
    if (isEditMode) {
      return `Update ${title.replace("Edit ", "").replace("Add ", "")}`;
    }
    return `Add ${title.replace("Add ", "").replace("Edit ", "")}`;
  };

  return (
    <BasePopup
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      description={description}
      error={error}
      w="900px"
    >
      {/* Search Form - Hide in edit mode if we already have a person */}
      {/* {(!isEditMode || !existingData?.person) && ( */}
      <form onSubmit={handleSearch}>
        <div className="grid grid-cols-3 gap-4 mb-4">
          <div className="flex flex-col">
            <label className="referral-popup-label">Fayda Number</label>
            <input
              type="text"
              className="referral-form-field"
              onChange={(e) => setSearchFayda(e.target.value)}
              name="fayda"
              placeholder="Fayda Number"
              value={searchFayda}
            />
          </div>
          <div className="flex flex-col">
            <label className="referral-popup-label">Phone Number</label>
            <input
              type="text"
              className="referral-form-field"
              onChange={(e) => setSearchPhone(e.target.value)}
              name="phone"
              placeholder="Phone Number"
              value={searchPhone}
            />
          </div>
          <div className="flex flex-col justify-end">
            <button
              type="submit"
              disabled={searchLoading}
              className={`referral-form-button h-10 ${
                searchLoading ? "opacity-50 cursor-not-allowed" : ""
              }`}
            >
              {searchLoading ? "Searching..." : "Search"}
            </button>
          </div>
        </div>
      </form>
      {/* )} */}

      {/* Person Selection - Show in both modes */}
      {Array.isArray(searchedPersons) &&
        searchedPersons.length > 0 &&
        !isEditMode && (
          <div className="flex flex-col mb-4">
            <label className="referral-popup-label">Select Person</label>
            <select
              className="referral-form-field"
              onChange={(e) => onPersonSelect(e.target.value)}
              value={selectedPersonId || ""}
            >
              <option value="">Select a person</option>
              {searchedPersons.map((person) => (
                <option key={person.id_number} value={person.id_number}>
                  {person.full_name} - {person.phone}
                </option>
              ))}
            </select>
          </div>
        )}

      {/* Action Buttons */}
      <div className="flex gap-2 mb-4">
        {showAddUserButton && (
          <button
            type="button"
            onClick={handleAddUser}
            className="referral-form-button bg-green-600 hover:bg-green-700 flex-1"
          >
            Add New User
          </button>
        )}

      </div>

      {/* User Form - Show when adding or editing a user */}
      {showUserForm && (
        <div className="mb-6 p-4 border rounded-lg bg-gray-50">
          <UserForm
            userData={isEditingPerson ? selectedPerson : null}
            onClose={handleUserFormClose}
            onSubmit={handleUserSubmit}
            loading={userFormLoading}
            // isEditMode={isEditingPerson}
          />
        </div>
      )}

      {/* Display selected user info */}
      {selectedPerson && !showUserForm && (
        <div className="mb-4 p-4 border rounded-lg bg-[#f5f5f5]">
          <div className="flex justify-between items-start">
            <div>
              <h3 className="font-semibold text-black mb-2">
                {isEditMode ? "Current Person:" : "Selected Person:"}
              </h3>
              <p>
                <strong>Name:</strong> {selectedPerson.full_name}
              </p>
              <p>
                <strong>ID:</strong> {selectedPerson.id_number}
              </p>
              <p>
                <strong>Phone:</strong> {selectedPerson.phone}
              </p>
              <p>
                <strong>Email:</strong> {selectedPerson.email}
              </p>
            </div>
            
          </div>
        </div>
      )}

    
      <form onSubmit={handleSubmit}>
        <div className={isFormDisabled && !isEditMode ? "opacity-50" : ""}>
          {children}
        </div>

        <button
          type="submit"
          disabled={loading || (isFormDisabled && !isEditMode)}
          className={`w-full mt-6 mb-4 referral-form-button ${
            loading || (isFormDisabled && !isEditMode)
              ? "opacity-50 cursor-not-allowed"
              : ""
          }`}
        >
          {getSubmitButtonText()}
        </button>
      </form>

      {/* Warning message when no person selected (only in add mode) */}
      {isFormDisabled && hasSearched && !isEditMode && (
        <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
          <p className="text-yellow-700 text-sm">
            Please select or create a person before adding information.
          </p>
        </div>
      )}

    </BasePopup>
  );
};
